import { useEffect, useRef } from 'react';

/**
 * Plays a loud repeating alert tone + vibration when a ride offer is active.
 * Stops automatically when `active` becomes false.
 */
export function useRideOfferAlert(active: boolean) {
  const audioCtxRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const vibrateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!active) {
      cleanup();
      return;
    }

    // Create audio context on first offer
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    audioCtxRef.current = ctx;

    const playBeep = () => {
      try {
        const now = ctx.currentTime;
        // Two-tone beep for urgency
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain = ctx.createGain();

        osc1.type = 'square';
        osc1.frequency.value = 880;
        osc2.type = 'square';
        osc2.frequency.value = 1320;

        gain.gain.setValueAtTime(0.35, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.6);

        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(ctx.destination);

        osc1.start(now);
        osc1.stop(now + 0.15);
        osc2.start(now + 0.2);
        osc2.stop(now + 0.35);
      } catch (_) {
        // Audio context may be closed
      }
    };

    // Play immediately then repeat every 1.5s
    playBeep();
    intervalRef.current = setInterval(playBeep, 1500);

    // Vibration pattern: buzz-pause-buzz, repeat
    const vibrate = () => {
      if (navigator.vibrate) {
        navigator.vibrate([300, 150, 300, 150, 300]);
      }
    };
    vibrate();
    vibrateIntervalRef.current = setInterval(vibrate, 2000);

    return cleanup;
  }, [active]);

  const cleanup = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (vibrateIntervalRef.current) {
      clearInterval(vibrateIntervalRef.current);
      vibrateIntervalRef.current = null;
    }
    if (navigator.vibrate) navigator.vibrate(0);
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  };
}
